import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import { CustomButton } from '../Infra/CustomButton/CustomButton';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  CheckBox as CheckBoxIcon,
  RemoveRedEye as VisibilityIcon,
  PushPin as PushPinIcon,
  SwapVert as SortIcon,
  ViewColumn as FilterListIcon,
} from '@mui/icons-material';
import { SavedView } from '../../types/ViewTypes';
import {
  GridFilterModel,
  GridSortModel,
  GridColumnVisibilityModel,
  GridColDef,
  GridPinnedColumns,
} from '@mui/x-data-grid-premium';

interface SaveViewDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (viewName: string, autoShowNewDates?: boolean) => void;
  editingView: SavedView | null;
  editMode?: 'rename' | 'update';
  currentSearchTerm: string;
  showOnlyIllioDifferences?: boolean;
  hideEmptyDateColumns?: boolean;
  autoShowNewDates?: boolean;
  autosizeColumns?: boolean;
  isGroupingEnabled?: boolean;
  selectedRows?: string[];
  columnFilters?: GridFilterModel;
  sortModel?: GridSortModel;
  columnVisibility?: GridColumnVisibilityModel;
  pinnedColumns?: GridPinnedColumns;
  columns?: GridColDef[];
  savedViews?: SavedView[];
}

const SaveViewDialog: React.FC<SaveViewDialogProps> = ({
  open,
  onClose,
  onSave,
  editingView,
  editMode = 'update',
  currentSearchTerm,
  showOnlyIllioDifferences,
  hideEmptyDateColumns,
  autoShowNewDates,
  autosizeColumns,
  isGroupingEnabled,
  selectedRows = [],
  columnFilters = { items: [] },
  sortModel = [],
  columnVisibility = {},
  pinnedColumns = { left: [], right: [] },
  columns = [],
  savedViews = [],
}) => {
  const [viewName, setViewName] = useState('');
  const [autoShowNewDatesChecked, setAutoShowNewDatesChecked] = useState(true);
  const [nameError, setNameError] = useState('');

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open && editingView) {
      setViewName(editingView.name);
      setAutoShowNewDatesChecked(editingView.autoShowNewDates ?? true);
    } else if (open) {
      setViewName('');
      setAutoShowNewDatesChecked(autoShowNewDates ?? true);
    }
    setNameError('');
  }, [open, editingView, autoShowNewDates]);

  // Validate view name for duplicates
  const validateViewName = (name: string) => {
    if (!name.trim()) {
      setNameError('');
      return false;
    }

    const isDuplicate = savedViews.some(view => {
      // For update mode, exclude the current view being edited
      if (editMode === 'update' && editingView && view.id === editingView.id) {
        return false;
      }
      return view.name.toLowerCase() === name.trim().toLowerCase();
    });

    if (isDuplicate) {
      setNameError('A view with this name already exists');
      return false;
    }

    setNameError('');
    return true;
  };

  const handleSave = () => {
    if (viewName.trim() && validateViewName(viewName)) {
      if (autoShowNewDates !== undefined) {
        onSave(viewName, autoShowNewDatesChecked);
      } else {
        onSave(viewName);
      }
      setViewName('');
      setAutoShowNewDatesChecked(true);
      setNameError('');
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setViewName(newName);
    // Validate on change to provide immediate feedback
    validateViewName(newName);
  };

  const hasColumnFilters = () => {
    return (
      columnFilters && columnFilters.items && columnFilters.items.length > 0
    );
  };

  const formatSortModelDescription = () => {
    if (!sortModel || sortModel.length === 0) {
      return 'Default sort order';
    }

    if (sortModel.length === 1) {
      const sort = sortModel[0];
      return `Sorted by ${sort.field} (${
        sort.sort === 'asc' ? 'ascending' : 'descending'
      })`;
    }

    return `${sortModel.length} columns sorted`;
  };

  const formatColumnVisibilityDescription = () => {
    if (!columnVisibility || Object.keys(columnVisibility).length === 0) {
      return 'All columns visible';
    }

    // Count hidden columns
    const hiddenCount = Object.values(columnVisibility).filter(
      visible => !visible
    ).length;

    if (hiddenCount === 0) {
      return 'All columns visible';
    } else if (hiddenCount === 1) {
      // Find the hidden column name
      const hiddenField = Object.entries(columnVisibility).find(
        ([, visible]) => !visible
      )?.[0];
      const hiddenColumn = columns.find(col => col.field === hiddenField);
      return `1 column hidden (${hiddenColumn?.headerName || hiddenField})`;
    } else {
      return `${hiddenCount} columns hidden`;
    }
  };

  const formatPinnedColumnsDescription = () => {
    const leftCount = pinnedColumns.left?.length || 0;
    const rightCount = pinnedColumns.right?.length || 0;

    if (leftCount === 0 && rightCount === 0) {
      return 'No pinned columns';
    }

    const parts = [];
    if (leftCount > 0) {
      parts.push(`${leftCount} column${leftCount > 1 ? 's' : ''} pinned left`);
    }
    if (rightCount > 0) {
      parts.push(
        `${rightCount} column${rightCount > 1 ? 's' : ''} pinned right`
      );
    }

    return parts.join(', ');
  };

  return (
    <Dialog
      data-testid="dialog-save-view"
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        data-testid="dialog-save-view-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {editingView
              ? editMode === 'rename'
                ? 'Rename View'
                : 'Update View'
              : 'Save Current View'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Save your current table configuration for future use
          </Typography>
        </Box>
        <IconButton
          data-testid="btn-close-save-view-dialog"
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            View Name
          </Typography>
          <TextField
            data-testid="input-view-name"
            autoFocus
            placeholder="Enter a descriptive name for this view"
            type="text"
            fullWidth
            size="small"
            value={viewName}
            onChange={handleNameChange}
            variant="outlined"
            error={!!nameError}
            helperText={nameError}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              backgroundColor: 'grey.50',
              p: 2,
              borderRadius: 1,
            }}
          >
            {autoShowNewDates !== undefined && (
              <FormControlLabel
                data-testid="checkbox-auto-show-dates"
                control={
                  <Checkbox
                    data-testid="checkbox-auto-show-dates-input"
                    checked={autoShowNewDatesChecked}
                    onChange={e => setAutoShowNewDatesChecked(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">Auto-show new dates</Typography>
                    <Tooltip title="When enabled, newly added date columns will automatically be displayed in the view">
                      <InfoIcon
                        sx={{ fontSize: 16, color: 'text.secondary' }}
                      />
                    </Tooltip>
                  </Box>
                }
                sx={{ width: '100%' }}
              />
            )}
            {hideEmptyDateColumns !== undefined && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hideEmptyDateColumns}
                    color="primary"
                    disabled
                  />
                }
                label={
                  <Typography variant="body2">Hide Empty Dates</Typography>
                }
                sx={{ width: '100%' }}
              />
            )}
            {showOnlyIllioDifferences !== undefined && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showOnlyIllioDifferences}
                    color="primary"
                    disabled
                  />
                }
                label={
                  <Typography variant="body2">
                    Show only rows to update in Illio
                  </Typography>
                }
                sx={{ width: '100%' }}
              />
            )}
            {autosizeColumns !== undefined && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autosizeColumns}
                    color="primary"
                    disabled
                  />
                }
                label={<Typography variant="body2">Autosize</Typography>}
                sx={{ width: '100%' }}
              />
            )}
            {isGroupingEnabled !== undefined && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isGroupingEnabled || false}
                    color="primary"
                    disabled
                  />
                }
                label={<Typography variant="body2">Groups</Typography>}
                sx={{ width: '100%' }}
              />
            )}
          </Box>
        </Box>

        <Box>
          <Typography
            variant="body1"
            sx={{ mb: 3, fontWeight: 600, fontSize: '1.1rem' }}
          >
            View Configuration
          </Typography>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 2,
              py: 1,
            }}
          >
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">Search Term</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {currentSearchTerm || 'None'}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckBoxIcon
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Typography variant="body2">Selected Rows</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {selectedRows.length > 0
                    ? `${selectedRows.length} row(s) selected`
                    : 'All rows shown'}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VisibilityIcon
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Typography variant="body2">Column Visibility</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatColumnVisibilityDescription()}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PushPinIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">Pinned Columns</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatPinnedColumnsDescription()}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SortIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2">Sorting</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatSortModelDescription()}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterListIcon
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Typography variant="body2">Column Filters</Typography>
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ...(!hasColumnFilters() ? {} : {}) }}
                >
                  {hasColumnFilters()
                    ? `${columnFilters.items.length} column filter(s) applied`
                    : 'None applied'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <CustomButton
          data-testid="btn-cancel-save-view"
          onClick={onClose}
          variant="text"
          sx={{ color: 'text.secondary' }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          data-testid="btn-save-view"
          onClick={handleSave}
          variant="contained"
          disabled={!viewName.trim()}
        >
          {editingView
            ? editMode === 'rename'
              ? 'Rename'
              : 'Update View'
            : 'Save View'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default SaveViewDialog;
